if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.handleContentClick = this.handleContentClick.bind(this);
        this.handleThumbnailClick = this.handleThumbnailClick.bind(this);
        this.handleContentScroll = this.handleContentScroll.bind(this);
        this.handleTouchStart = this.handleTouchStart.bind(this);
        this.handleTouchMove = this.handleTouchMove.bind(this);
        this.handleTouchEnd = this.handleTouchEnd.bind(this);
        this.handleNavClick = this.handleNavClick.bind(this);
        this.scrollListenerOptions = { passive: true };
        this.touchListenerOptions = { passive: true };
        this.scrollAnimationFrame = null;
        this.suppressScrollHandler = false;
        this.suppressScrollTimeout = null;
        this.currentMediaId = null;
        this.touchTracking = {
          active: false,
          startX: 0,
          startY: 0,
          swiped: false,
        };
      }

      connectedCallback() {
        if (super.connectedCallback) super.connectedCallback();
        this.refreshMediaElements();
        this.attachListeners();
      }

      hide() {
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      refreshMediaElements() {
        this.content = this.querySelector('.product-media-modal__content');
        this.thumbnailContainer = this.querySelector('.product-media-modal__thumbnails');
        this.thumbnailButtons = this.thumbnailContainer
          ? Array.from(this.thumbnailContainer.querySelectorAll('button[data-media-id]'))
          : [];
        this.navButtons = Array.from(this.querySelectorAll('[data-modal-nav]'));
        const activeMedia =
          this.content?.querySelector('[data-media-id].active') ||
          this.content?.querySelector('[data-media-id]');
        this.currentMediaId = activeMedia?.dataset?.mediaId || null;
      }

      attachListeners() {
        if (this.content) {
          this.content.removeEventListener('click', this.handleContentClick);
          this.content.addEventListener('click', this.handleContentClick);
          this.content.removeEventListener('scroll', this.handleContentScroll, this.scrollListenerOptions);
          this.content.addEventListener('scroll', this.handleContentScroll, this.scrollListenerOptions);
          this.content.removeEventListener('touchstart', this.handleTouchStart, this.touchListenerOptions);
          this.content.removeEventListener('touchmove', this.handleTouchMove, this.touchListenerOptions);
          this.content.removeEventListener('touchend', this.handleTouchEnd);
          this.content.removeEventListener('touchcancel', this.handleTouchEnd);
          this.content.addEventListener('touchstart', this.handleTouchStart, this.touchListenerOptions);
          this.content.addEventListener('touchmove', this.handleTouchMove, this.touchListenerOptions);
          this.content.addEventListener('touchend', this.handleTouchEnd);
          this.content.addEventListener('touchcancel', this.handleTouchEnd);
        }

        if (this.thumbnailContainer) {
          this.thumbnailContainer.removeEventListener('click', this.handleThumbnailClick);
          this.thumbnailContainer.addEventListener('click', this.handleThumbnailClick);
        }

        if (this.navButtons?.length) {
          this.navButtons.forEach((button) => {
            button.removeEventListener('click', this.handleNavClick);
            button.addEventListener('click', this.handleNavClick);
          });
        }
      }

      showActiveMedia(mediaId = this.openedBy?.getAttribute('data-media-id')) {
        this.refreshMediaElements();
        this.attachListeners();

        if (!mediaId) return;

        const activeMedia = this.content?.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;

        this.applyActiveState(activeMedia, { scrollIntoView: true, syncThumbnails: true });
        this.updateNavButtons(mediaId);
      }

      handleContentClick(event) {
        const image = event.target instanceof HTMLElement ? event.target.closest('img') : null;
        if (!image) return;
        if (!this.content?.contains(image)) return;
        if (image.closest('.deferred-media__poster')) return;
        this.hide();
      }

      handleTouchStart(event) {
        if (!event.touches || event.touches.length !== 1) return;
        const touch = event.touches[0];
        this.touchTracking = {
          active: true,
          startX: touch.clientX,
          startY: touch.clientY,
          swiped: false,
        };
      }

      handleTouchMove(event) {
        if (!this.touchTracking.active || !event.touches || event.touches.length !== 1) return;
        if (this.touchTracking.swiped) return;

        const touch = event.touches[0];
        const deltaX = touch.clientX - this.touchTracking.startX;
        const deltaY = touch.clientY - this.touchTracking.startY;

        const swipeThreshold = 70;
        if (deltaY > swipeThreshold && Math.abs(deltaY) > Math.abs(deltaX)) {
          this.touchTracking.swiped = true;
          this.hide();
        }
      }

      handleTouchEnd() {
        this.touchTracking.active = false;
      }

      handleNavClick(event) {
        event.preventDefault();
        const button = event.currentTarget;
        if (!button || button.disabled) return;

        const direction = button.dataset.modalNav;
        if (!direction || !this.content) return;

        const mediaItems = this.getOrderedMediaItems();
        if (!mediaItems.length || !this.currentMediaId) return;

        const currentIndex = mediaItems.findIndex(
          (element) => element.dataset?.mediaId === this.currentMediaId
        );
        if (currentIndex === -1) return;

        const nextIndex =
          direction === 'next' ? currentIndex + 1 : direction === 'prev' ? currentIndex - 1 : currentIndex;

        if (nextIndex < 0 || nextIndex >= mediaItems.length) return;

        const targetMedia = mediaItems[nextIndex];
        if (targetMedia) this.applyActiveState(targetMedia, { scrollIntoView: true, syncThumbnails: true });
      }

      handleContentScroll() {
        if (this.suppressScrollHandler) return;
        if (this.scrollAnimationFrame) cancelAnimationFrame(this.scrollAnimationFrame);
        this.scrollAnimationFrame = requestAnimationFrame(() => {
          this.scrollAnimationFrame = null;
          this.setActiveMediaFromScroll();
        });
      }

      handleThumbnailClick(event) {
        const button =
          event.target instanceof HTMLElement ? event.target.closest('button[data-media-id]') : null;
        if (!button || !this.thumbnailContainer?.contains(button)) return;
        event.preventDefault();
        this.showActiveMedia(button.dataset.mediaId);
      }

      setActiveMediaFromScroll() {
        if (!this.content) return;
        const mediaItems = this.getOrderedMediaItems();
        if (!mediaItems.length) return;

        const containerCenter = this.content.scrollLeft + this.content.clientWidth / 2;
        let closest = mediaItems[0];
        let closestDistance = Math.abs(
          containerCenter - (closest.offsetLeft + closest.clientWidth / 2)
        );

        for (let i = 1; i < mediaItems.length; i++) {
          const element = mediaItems[i];
          const elementCenter = element.offsetLeft + element.clientWidth / 2;
          const distance = Math.abs(containerCenter - elementCenter);
          if (distance < closestDistance) {
            closest = element;
            closestDistance = distance;
          }
        }

        const mediaId = closest.dataset?.mediaId;
        if (!mediaId || mediaId === this.currentMediaId) return;

        this.applyActiveState(closest, { scrollIntoView: false, syncThumbnails: true });
      }

      applyActiveState(activeMedia, { scrollIntoView = true, syncThumbnails = true } = {}) {
        if (!activeMedia) return;

        const mediaId = activeMedia.dataset?.mediaId;
        if (!mediaId) return;

        if (this.content) {
          this.content.querySelectorAll('[data-media-id]').forEach((element) => {
            if (element === activeMedia) {
              element.classList.add('active');
            } else {
              element.classList.remove('active');
            }
          });
        }

        this.currentMediaId = mediaId;

        if (scrollIntoView) {
          this.suppressScrollHandler = true;
          if (this.suppressScrollTimeout) clearTimeout(this.suppressScrollTimeout);
          const container = this.content || this.querySelector('[role="document"]');
          if (container) {
            const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            const targetLeft =
              activeMedia.offsetLeft - (container.clientWidth - activeMedia.clientWidth) / 2;
            if (typeof container.scrollTo === 'function') {
              container.scrollTo({
                left: Math.max(targetLeft, 0),
                top: 0,
                behavior: prefersReducedMotion ? 'auto' : 'smooth',
              });
            } else {
              container.scrollLeft = Math.max(targetLeft, 0);
              container.scrollTop = 0;
            }
          } else {
            activeMedia.scrollIntoView({ block: 'nearest', inline: 'center' });
          }
          this.suppressScrollTimeout = setTimeout(() => {
            this.suppressScrollHandler = false;
          }, 350);
        }

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        ) {
          activeMedia.loadContent();
        }

        if (syncThumbnails) {
          this.updateActiveThumbnail(mediaId, { ensureVisible: scrollIntoView });
        }

        this.updateNavButtons(mediaId);
      }

      updateActiveThumbnail(mediaId, { ensureVisible = true } = {}) {
        if (!this.thumbnailButtons?.length) return;

        this.thumbnailButtons.forEach((button) => {
          const isActive = button.dataset.mediaId === mediaId;

          button.classList.toggle('is-active', isActive);
          if (isActive) {
            button.setAttribute('aria-current', 'true');

            if (this.thumbnailContainer && ensureVisible) {
              const targetLeft =
                button.offsetLeft - (this.thumbnailContainer.clientWidth - button.clientWidth) / 2;
              const buttonLeft = button.offsetLeft;
              const buttonRight = buttonLeft + button.clientWidth;
              const containerLeft = this.thumbnailContainer.scrollLeft;
              const containerRight = containerLeft + this.thumbnailContainer.clientWidth;

              if (buttonLeft < containerLeft || buttonRight > containerRight) {
                if (typeof this.thumbnailContainer.scrollTo === 'function') {
                  this.thumbnailContainer.scrollTo({
                    left: Math.max(targetLeft, 0),
                    behavior: 'smooth',
                  });
                } else {
                  this.thumbnailContainer.scrollLeft = Math.max(targetLeft, 0);
                }
              }
            }
          } else {
            button.removeAttribute('aria-current');
          }
        });
      }

      updateNavButtons(mediaId) {
        if (!this.navButtons?.length || !this.content) return;
        const mediaItems = this.getOrderedMediaItems();
        if (!mediaItems.length) {
          this.navButtons.forEach((button) => button.setAttribute('disabled', 'true'));
          return;
        }

        const currentIndex = mediaItems.findIndex((element) => element.dataset?.mediaId === mediaId);
        const atStart = currentIndex <= 0;
        const atEnd = currentIndex === mediaItems.length - 1;

        this.navButtons.forEach((button) => {
          if (button.dataset.modalNav === 'prev') {
            button.toggleAttribute('disabled', atStart);
          } else if (button.dataset.modalNav === 'next') {
            button.toggleAttribute('disabled', atEnd);
          }
        });
      }

      getOrderedMediaItems() {
        if (!this.content) return [];
        return Array.from(this.content.querySelectorAll('[data-media-id]'));
      }
    }
  );
}
